import { useContext, useEffect, useRef, useState } from "react"
import HRStarsInput from "./HRStarsInput";
import CustomModal from "../components/modal";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { UserDataContext } from "../context/UserContextProvider";
import { ToastDataContext } from "../context/ToastContextProvider";
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { hrFeedbackEndpoint } from "../hr_endpoints";

export default function HRFeedbackForm() {

    const { t } = useTranslation();

    dayjs.extend(utc)
    dayjs.extend(timezone)

    const [feedback, setFeedback] = useState(null)
    const [open, setOpen] = useState(false)
    const [rating, setRating] = useState(0)
    const [ratingMissing, setRatingMissing] = useState(false)
    // Cancelling records a 0-rating row so the prompt stops coming back today.
    // It goes through the same submit, so it has to be told apart from someone
    // pressing Trimite without choosing a star.
    const dismissing = useRef(false)

    const userContext = useContext(UserDataContext)
    const toastContext = useContext(ToastDataContext)
    const navigate = useNavigate()
    const formRef = useRef(null)

    const onCancel = () => {
        dismissing.current = true
        reset({ comment: '' })
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }

    const onSubmit = async (data) => {
        const dismissed = dismissing.current
        dismissing.current = false

        // A rating of 0 is how a dismissal is stored, and the admin feedback
        // list only shows ratings above 0 — so sending one from Trimite would
        // throw the visit away silently. Ask for the star instead.
        if (!dismissed && rating === 0) {
            setRatingMissing(true)
            return
        }

        const response = await fetch(hrFeedbackEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${userContext.user?.token}`
            },
            body: JSON.stringify({
                slot_id: feedback?.id,
                rating: dismissed ? 0 : rating,
                comment: data.comment
            })
        })

        if (response.status === 401) {
            userContext.setUserData({})
            navigate('/login')
            toastContext.error(t('sessionExpired'));
        }

        if (response.ok) {
            toastContext.success(t('dashboard.feedbackSuccess'))
        } else {
            const serverMessage = await response.text()
            toastContext.error(serverMessage || t('dashboard.feedbackError'))
        }
        setOpen(false)
        setFeedback(null)
        setRating(0)
        setRatingMissing(false)
        reset()
    }

    const { register, reset, handleSubmit } = useForm()

    useEffect(() => {
        fetch(hrFeedbackEndpoint, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${userContext.user?.token}`
            }
        })
            .then(response => {
                if (response.status === 401) {
                    userContext.setUserData({})
                    navigate('/login')
                    toastContext.error(t('sessionExpired'));
                }

                return response.json()
            })
            .then(data => {
                if (data) {
                    setFeedback(data)
                    setOpen(true)
                }
            })
    }, [])

    return (
        <CustomModal open={open} setOpen={setOpen} title={t('dashboard.feedbackTitle')} cancelButton={t('dashboard.feedbackCancelButton')} confirmButton={t('dashboard.feedbackConfirmButton')} onConfirm={() => {
            formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }} onCancel={onCancel} noOutsideClick={true} withIcon={false} fullWidth={true}>
            <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-5">
                    {/* What the feedback is about, as a block to glance at rather
                        than a sentence to read. */}
                    <div className="rounded-xl border border-neutral-border bg-neutral-100 px-4 py-3">
                        <p className="font-text text-neutral-500">{t('dashboard.feedbackDescription')}</p>
                        <p className="mt-1 font-subtitle text-neutral-1100">
                            {dayjs.tz(feedback?.start_time, 'Europe/Bucharest').format('DD-MM-YYYY')}
                            {" · "}
                            {dayjs.tz(feedback?.start_time, 'Europe/Bucharest').format('HH:mm')}
                            {" - "}
                            {dayjs.tz(feedback?.end_time, 'Europe/Bucharest').format('HH:mm')}
                        </p>
                        <p className="mt-1 font-text text-neutral-500">
                            {t('dashboard.therapist')}{" "}
                            <span className="text-neutral-1100">{feedback?.therapist_name}</span>
                        </p>
                    </div>

                    <div>
                        <label className="block font-subtitle text-neutral-1100 text-center">
                            {t('dashboard.rating')}
                        </label>
                        <div className="mt-2">
                            <HRStarsInput
                                value={rating}
                                onChange={(value) => { setRating(value); setRatingMissing(false) }}
                                labels={[
                                    t('dashboard.rating1'),
                                    t('dashboard.rating2'),
                                    t('dashboard.rating3'),
                                    t('dashboard.rating4'),
                                    t('dashboard.rating5'),
                                ]}
                            />
                        </div>
                        {ratingMissing && (
                            <p className="mt-1 text-center font-text text-tertiary">
                                {t('dashboard.ratingRequired')}
                            </p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="comment" className="block font-subtitle text-neutral-1100">
                            {t('dashboard.comment')}{" "}
                            <span className="font-text text-neutral-400">{t('dashboard.commentOptional')}</span>
                        </label>
                        <textarea
                            id="comment"
                            {...register('comment')}
                            placeholder={t('dashboard.commentPlaceholder')}
                            rows={3}
                            className="mt-2 w-full resize-none rounded-lg border border-neutral-border px-3 py-2 font-text text-neutral-1100 placeholder:text-neutral-400 focus:border-primary focus:outline-none"
                        />
                    </div>

                    {/* The window is short and easy to miss, so the rule sits where
                        a footnote belongs rather than above the form. */}
                    <p className="font-text text-neutral-400">
                        {t('dashboard.feedbackSameDayNotice')}
                    </p>
                </div>
            </form>
        </CustomModal>
    )
}